import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { db } from "../../../firebase/firebaseConfig";
import StudentHistoryDrawer from "./StudentHistoryDrawer";
import {
  FiAlertTriangle,
  FiBarChart2,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiCopy,
  FiDownload,
  FiFileText,
  FiAward,
  FiSearch,
  FiTrendingUp,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";

function displayText(value, fallback = "—") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
}

function studentName(stu) {
  return (
    stu.fullName ||
    stu.name ||
    stu.display?.name ||
    `${stu.firstName || ""} ${stu.lastName || ""}`.trim() ||
    stu.email ||
    stu.id ||
    "Học viên"
  );
}

function tsToMs(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts > 1e12 ? ts : ts * 1000;
  if (typeof ts === "string") {
    const ms = new Date(ts).getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    if (typeof ts._seconds === "number") return ts._seconds * 1000;
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
  }
  return 0;
}

function fmtDate(value, fallback = "—") {
  const ms = tsToMs(value);
  return ms ? dayjs(ms).format("DD/MM/YYYY") : fallback;
}

function getPercent(item) {
  if (typeof item?.percent === "number" && Number.isFinite(item.percent)) return Math.round(item.percent);
  if (typeof item?.score?.percent === "number" && Number.isFinite(item.score.percent)) return Math.round(item.score.percent);

  const correct = typeof item?.score?.correct === "number" ? item.score.correct : null;
  const total = typeof item?.score?.total === "number" ? item.score.total : null;
  if (typeof correct === "number" && typeof total === "number" && total > 0) {
    return Math.round((correct / total) * 100);
  }
  return null;
}

function getDurationSeconds(item) {
  const value = item?.duration ?? item?.timeSpent ?? item?.elapsedSeconds;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.round(seconds);
}

function fmtDuration(seconds, fallback = "—") {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) return fallback;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins ? `${hours}h ${remMins}′` : `${hours}h`;
  }
  if (mins <= 0) return `${secs}s`;
  return secs ? `${mins}′ ${secs}s` : `${mins}′`;
}

function isSubmitted(history) {
  const status = (history?.status || "").toLowerCase();
  return status === "submitted" || status === "finished" || !!history?.submittedAt;
}

function isAssignmentOverdue(assignment) {
  const deadlineMs = tsToMs(assignment?.deadline);
  if (!deadlineMs) return false;
  return dayjs(deadlineMs).endOf("day").isBefore(dayjs());
}

function average(nums) {
  const valid = nums.filter((n) => typeof n === "number" && Number.isFinite(n));
  if (!valid.length) return null;
  return Math.round(valid.reduce((sum, n) => sum + n, 0) / valid.length);
}

function chunkArray(arr = [], size = 10) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function statusBadge(status) {
  if (status === "good") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "warning") return "border-amber-200 bg-amber-50 text-amber-700";
  if (status === "danger") return "border-red-200 bg-red-50 text-red-700";
  return "border-slate-200 bg-slate-100 text-slate-600";
}

function StatCard({ icon: Icon, label, value, hint, tone = "blue" }) {
  const toneClass =
    tone === "green"
      ? "text-emerald-700 bg-emerald-50 border-emerald-100"
      : tone === "amber"
      ? "text-amber-700 bg-amber-50 border-amber-100"
      : tone === "red"
      ? "text-red-700 bg-red-50 border-red-100"
      : "text-blue-700 bg-blue-50 border-blue-100";

  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-500">{label}</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
        </div>
        {Icon && (
          <div className={`rounded-xl border p-2 ${toneClass}`}>
            <Icon size={20} />
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressBar({ value, tone = "blue" }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const bar = tone === "green" ? "bg-emerald-600" : tone === "amber" ? "bg-amber-500" : tone === "red" ? "bg-red-500" : "bg-blue-600";
  return (
    <div className="h-2 overflow-hidden rounded-full bg-slate-200">
      <div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function RankingList({ title, icon: Icon, rows = [], valueText, hintText, emptyText = "Chưa đủ dữ liệu xếp hạng." }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h4 className="flex items-center gap-2 text-base font-bold text-slate-900">
          {Icon && <Icon className="text-blue-600" />} {title}
        </h4>
      </div>

      {rows.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed p-4 text-sm text-slate-500">{emptyText}</div>
      ) : (
        <div className="mt-4 space-y-2">
          {rows.map((row, index) => (
            <div key={row.student.id} className="flex items-center gap-3 rounded-xl border bg-slate-50 p-3">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  index === 0
                    ? "bg-amber-100 text-amber-700"
                    : index === 1
                    ? "bg-blue-100 text-blue-700"
                    : index === 2
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {index + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-slate-900">{studentName(row.student)}</div>
                {hintText && <div className="mt-0.5 text-xs text-slate-500">{hintText(row)}</div>}
              </div>
              <div className="shrink-0 text-right text-sm font-bold text-blue-700">{valueText(row)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function useTeacherReportData(classInfo) {
  const [assignments, setAssignments] = useState([]);
  const [attendanceDocs, setAttendanceDocs] = useState([]);
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!classInfo?.id) {
      setAssignments([]);
      setAttendanceDocs([]);
      setHistories([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const unsubAssignments = onSnapshot(
      query(collection(db, "assignments"), where("classId", "==", classInfo.id)),
      (snap) => {
        setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Cannot load assignments report", err);
        setLoading(false);
      }
    );

    const unsubAttendance = onSnapshot(
      query(collection(db, "attendance"), where("classId", "==", classInfo.id)),
      (snap) => setAttendanceDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Cannot load attendance report", err)
    );

    return () => {
      unsubAssignments && unsubAssignments();
      unsubAttendance && unsubAttendance();
    };
  }, [classInfo?.id]);

  useEffect(() => {
    if (!classInfo?.id) {
      setHistories([]);
      return undefined;
    }

    const assignmentIds = assignments.map((a) => a.id).filter(Boolean);
    if (!assignmentIds.length) {
      setHistories([]);
      return undefined;
    }

    const stores = new Map();
    const unsubs = chunkArray(assignmentIds, 10).map((ids, index) => {
      const qRef = query(collection(db, "test_histories"), where("assignmentId", "in", ids));
      return onSnapshot(
        qRef,
        (snap) => {
          stores.set(index, snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          const merged = Array.from(stores.values()).flat();
          setHistories(Array.from(new Map(merged.map((h) => [h.id, h])).values()));
        },
        (err) => console.error("Cannot load test histories report", err)
      );
    });

    return () => unsubs.forEach((u) => u && u());
  }, [classInfo?.id, assignments]);

  return { assignments, attendanceDocs, histories, loading };
}

export default function TabReports({ classInfo, students = [] }) {
  const { assignments, attendanceDocs, histories, loading } = useTeacherReportData(classInfo);
  const [period, setPeriod] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedHistoryStudent, setSelectedHistoryStudent] = useState(null);

  const studentIds = useMemo(() => new Set(students.map((s) => s.id)), [students]);

  const fromMs = useMemo(() => {
    if (period === "30") return dayjs().subtract(30, "day").startOf("day").valueOf();
    if (period === "90") return dayjs().subtract(90, "day").startOf("day").valueOf();
    if (period === "month") return dayjs().startOf("month").valueOf();
    return 0;
  }, [period]);

  const scopedAttendanceDocs = useMemo(() => {
    return attendanceDocs.filter((doc) => {
      const dateMs = tsToMs(doc.date || doc.createdAt || doc.updatedAt);
      return !fromMs || !dateMs || dateMs >= fromMs;
    });
  }, [attendanceDocs, fromMs]);

  const scopedAssignments = useMemo(() => {
    return assignments.filter((assignment) => {
      const ms = tsToMs(assignment.deadline || assignment.startAt || assignment.createdAt);
      return !fromMs || !ms || ms >= fromMs;
    });
  }, [assignments, fromMs]);

  const scopedAssignmentIds = useMemo(() => new Set(scopedAssignments.map((a) => a.id)), [scopedAssignments]);

  const scopedHistories = useMemo(() => {
    return histories.filter((history) => {
      const belongsToAssignment = scopedAssignmentIds.has(history.assignmentId);
      const belongsToStudent = !history.userId || studentIds.has(history.userId);
      const ms = tsToMs(history.submittedAt || history.updatedAt || history.createdAt);
      return belongsToAssignment && belongsToStudent && (!fromMs || !ms || ms >= fromMs);
    });
  }, [histories, scopedAssignmentIds, studentIds, fromMs]);

  const attendanceByStudent = useMemo(() => {
    const map = new Map();
    students.forEach((s) => {
      map.set(s.id, { present: 0, late: 0, absent: 0, excused: 0, total: 0, notes: [] });
    });

    scopedAttendanceDocs.forEach((docItem) => {
      const rows = Array.isArray(docItem.students) ? docItem.students : [];
      rows.forEach((row) => {
        const id = row.studentId || row.uid || row.id;
        if (!id || !map.has(id)) return;
        const stat = map.get(id);
        const status = (row.status || "").toLowerCase();
        stat.total += 1;
        if (status === "present") stat.present += 1;
        else if (status === "late") stat.late += 1;
        else if (status === "excused") stat.excused += 1;
        else if (status === "absent") stat.absent += 1;
        else stat.present += 1;
        if (row.note) stat.notes.push(row.note);
      });
    });

    return map;
  }, [scopedAttendanceDocs, students]);

  const assignmentReports = useMemo(() => {
    return scopedAssignments
      .map((assignment) => {
        const targetIds = Array.isArray(assignment.assignedTo) && assignment.assignedTo.length
          ? assignment.assignedTo.filter((id) => studentIds.has(id))
          : students.map((s) => s.id);
        const targetSet = new Set(targetIds);
        const related = scopedHistories.filter((h) => h.assignmentId === assignment.id && targetSet.has(h.userId));
        const submitted = related.filter(isSubmitted);
        const submittedIds = new Set(submitted.map((h) => h.userId));
        const percents = submitted.map(getPercent).filter((x) => typeof x === "number");
        const durations = submitted.map(getDurationSeconds).filter((x) => typeof x === "number");
        const completion = targetIds.length ? Math.round((submittedIds.size / targetIds.length) * 100) : 0;

        return {
          assignment,
          targetCount: targetIds.length,
          submittedCount: submittedIds.size,
          missingCount: Math.max(0, targetIds.length - submittedIds.size),
          completion,
          avgPercent: average(percents),
          bestPercent: percents.length ? Math.max(...percents) : null,
          avgDuration: average(durations),
          minDuration: durations.length ? Math.min(...durations) : null,
          maxDuration: durations.length ? Math.max(...durations) : null,
          attempts: related.length,
          overdue: isAssignmentOverdue(assignment),
        };
      })
      .sort((a, b) => tsToMs(b.assignment.deadline || b.assignment.createdAt) - tsToMs(a.assignment.deadline || a.assignment.createdAt));
  }, [scopedAssignments, scopedHistories, studentIds, students]);

  const studentReports = useMemo(() => {
    return students
      .map((student) => {
        const att = attendanceByStudent.get(student.id) || { present: 0, late: 0, absent: 0, excused: 0, total: 0 };
        const attended = att.present + att.late;
        const attendanceRate = att.total ? Math.round((attended / att.total) * 100) : null;

        const assigned = scopedAssignments.filter((assignment) => {
          if (!Array.isArray(assignment.assignedTo) || assignment.assignedTo.length === 0) return true;
          return assignment.assignedTo.includes(student.id);
        });

        const studentHistories = scopedHistories.filter((h) => h.userId === student.id);
        const submitted = studentHistories.filter(isSubmitted);
        const submittedAssignmentIds = new Set(submitted.map((h) => h.assignmentId));
        const completed = assigned.filter((a) => submittedAssignmentIds.has(a.id));
        const missing = assigned.filter((a) => !submittedAssignmentIds.has(a.id));
        const completionRate = assigned.length ? Math.round((completed.length / assigned.length) * 100) : null;
        const percents = submitted.map(getPercent).filter((x) => typeof x === "number");
        const durations = submitted.map(getDurationSeconds).filter((x) => typeof x === "number");
        const avgPercent = average(percents);
        const bestPercent = percents.length ? Math.max(...percents) : null;
        const avgDuration = average(durations);
        const totalDuration = durations.reduce((sum, value) => sum + value, 0);
        const latest = studentHistories
          .slice()
          .sort((a, b) => tsToMs(b.submittedAt || b.updatedAt) - tsToMs(a.submittedAt || a.updatedAt))[0];

        const warnings = [];
        if (attendanceRate !== null && attendanceRate < 75) warnings.push("Chuyên cần thấp");
        if (completionRate !== null && completionRate < 70) warnings.push("Thiếu bài");
        if (avgPercent !== null && avgPercent < 60) warnings.push("Điểm thấp");
        if (assigned.length > 0 && studentHistories.length === 0) warnings.push("Chưa có hoạt động");

        return {
          student,
          attendance: att,
          attendanceRate,
          assignedCount: assigned.length,
          completedCount: completed.length,
          missingCount: missing.length,
          completionRate,
          avgPercent,
          bestPercent,
          avgDuration,
          totalDuration,
          attemptCount: submitted.length,
          latest,
          warnings,
          status: warnings.length >= 2 ? "danger" : warnings.length === 1 ? "warning" : "good",
        };
      })
      .sort((a, b) => {
        const level = { danger: 0, warning: 1, good: 2 };
        return level[a.status] - level[b.status] || studentName(a.student).localeCompare(studentName(b.student), "vi");
      });
  }, [students, attendanceByStudent, scopedAssignments, scopedHistories]);

  const filteredStudentReports = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return studentReports;
    return studentReports.filter((row) => {
      const name = studentName(row.student).toLowerCase();
      const email = (row.student.email || row.student.display?.email || "").toLowerCase();
      return `${name} ${email}`.includes(keyword);
    });
  }, [studentReports, search]);

  const overview = useMemo(() => {
    const avgAttendance = average(studentReports.map((r) => r.attendanceRate).filter((v) => v !== null));
    const avgCompletion = average(studentReports.map((r) => r.completionRate).filter((v) => v !== null));
    const avgScore = average(studentReports.map((r) => r.avgPercent).filter((v) => v !== null));
    const avgDuration = average(studentReports.map((r) => r.avgDuration).filter((v) => v !== null));
    const needAttention = studentReports.filter((r) => r.status === "danger" || r.status === "warning").length;

    return {
      totalStudents: students.length,
      activeStudents: students.filter((s) => (s.status || s.enrollment?.status || "active") === "active").length,
      sessions: scopedAttendanceDocs.length,
      assignments: scopedAssignments.length,
      avgAttendance,
      avgCompletion,
      avgScore,
      avgDuration,
      needAttention,
    };
  }, [students, studentReports, scopedAttendanceDocs, scopedAssignments]);

  const rankings = useMemo(() => {
    const topBy = (predicate, compare) =>
      studentReports
        .filter(predicate)
        .slice()
        .sort(compare)
        .slice(0, 3);

    return {
      avgScore: topBy(
        (r) => typeof r.avgPercent === "number",
        (a, b) => b.avgPercent - a.avgPercent || b.completedCount - a.completedCount
      ),
      completion: topBy(
        (r) => typeof r.completionRate === "number" && r.assignedCount > 0,
        (a, b) => b.completionRate - a.completionRate || b.completedCount - a.completedCount
      ),
      attendance: topBy(
        (r) => typeof r.attendanceRate === "number" && r.attendance.total > 0,
        (a, b) => b.attendanceRate - a.attendanceRate || b.attendance.total - a.attendance.total
      ),
      totalDuration: topBy(
        (r) => r.totalDuration > 0,
        (a, b) => b.totalDuration - a.totalDuration || b.attemptCount - a.attemptCount
      ),
      attempts: topBy(
        (r) => r.attemptCount > 0,
        (a, b) => b.attemptCount - a.attemptCount || b.totalDuration - a.totalDuration
      ),
    };
  }, [studentReports]);

  const selectedStudentReport = useMemo(() => {
    return studentReports.find((r) => r.student.id === selectedStudentId) || studentReports[0] || null;
  }, [selectedStudentId, studentReports]);

  useEffect(() => {
    if (!selectedStudentId && studentReports[0]?.student?.id) setSelectedStudentId(studentReports[0].student.id);
  }, [selectedStudentId, studentReports]);

  const parentReportText = useMemo(() => {
    if (!selectedStudentReport) return "";
    const r = selectedStudentReport;
    return [
      `BÁO CÁO HỌC TẬP - ${studentName(r.student)}`,
      `Lớp: ${displayText(classInfo?.name, classInfo?.id || "Lớp học")}`,
      `Thời gian: ${period === "all" ? "Toàn bộ" : period === "month" ? "Tháng này" : `${period} ngày gần đây`}`,
      "",
      `1. Chuyên cần: ${r.attendanceRate ?? "—"}% (${r.attendance.present + r.attendance.late}/${r.attendance.total || 0} buổi).`,
      `2. Bài tập/kiểm tra: đã hoàn thành ${r.completedCount}/${r.assignedCount} bài; còn thiếu ${r.missingCount} bài.`,
      `3. Điểm trung bình: ${r.avgPercent ?? "—"}%; điểm cao nhất: ${r.bestPercent ?? "—"}%.`,
      `4. Thời gian làm bài: trung bình ${fmtDuration(r.avgDuration)} mỗi lần; tổng thời gian đã ghi nhận ${fmtDuration(r.totalDuration)} trên ${r.attemptCount} lượt nộp.`,
      `5. Nhận xét nhanh: ${r.warnings.length ? r.warnings.join(", ") : "Học viên đang theo kịp tiến độ lớp."}`,
      "",
      "Giáo viên sẽ tiếp tục theo dõi và hỗ trợ học viên trong các buổi học tiếp theo.",
    ].join("\n");
  }, [selectedStudentReport, classInfo, period]);

  const handleCopyParentReport = async () => {
    if (!parentReportText) return;
    try {
      await navigator.clipboard.writeText(parentReportText);
      toast.success("Đã copy báo cáo phụ huynh.");
    } catch (err) {
      console.error(err);
      toast.error("Không thể copy. Hãy chọn và copy thủ công.");
    }
  };

  const handleExportCsv = () => {
    const header = ["Học viên", "Chuyên cần", "Bài đã nộp", "Bài được giao", "Bài còn thiếu", "Điểm TB", "Thời gian TB", "Tổng thời gian", "Cảnh báo"];
    const rows = filteredStudentReports.map((r) => [
      studentName(r.student),
      r.attendanceRate ?? "",
      r.completedCount,
      r.assignedCount,
      r.missingCount,
      r.avgPercent ?? "",
      fmtDuration(r.avgDuration, ""),
      fmtDuration(r.totalDuration, ""),
      r.warnings.join("; "),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `class-report-${classInfo?.id || "class"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-xl font-bold text-slate-900">
            <FiFileText className="text-blue-600" /> Báo cáo lớp học
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Theo dõi chuyên cần, tiến độ bài tập/kiểm tra, điểm số và danh sách học viên cần hỗ trợ.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="all">Toàn bộ khoá</option>
            <option value="month">Tháng này</option>
            <option value="30">30 ngày gần đây</option>
            <option value="90">90 ngày gần đây</option>
          </select>

          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <FiDownload /> Xuất CSV
          </button>
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border bg-slate-50 p-4 text-sm text-slate-500">Đang tải dữ liệu báo cáo...</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        <StatCard icon={FiUsers} label="Học viên" value={`${overview.activeStudents}/${overview.totalStudents}`} hint="Đang học / tổng số" />
        <StatCard icon={FiUserCheck} label="Chuyên cần TB" value={overview.avgAttendance === null ? "—" : `${overview.avgAttendance}%`} hint={`${overview.sessions} buổi đã điểm danh`} tone="green" />
        <StatCard icon={FiClipboard} label="Hoàn thành bài" value={overview.avgCompletion === null ? "—" : `${overview.avgCompletion}%`} hint={`${overview.assignments} bài đã giao`} tone="amber" />
        <StatCard icon={FiClock} label="Thời gian làm TB" value={fmtDuration(overview.avgDuration)} hint="Tính trên các lượt nộp" tone="blue" />
        <StatCard icon={FiAlertTriangle} label="Cần chú ý" value={overview.needAttention} hint="Dựa trên vắng, thiếu bài, điểm thấp" tone={overview.needAttention ? "red" : "green"} />
      </div>

      <section className="rounded-2xl border bg-white p-4 md:p-5 shadow-sm">
        <div>
          <h4 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <FiAward className="text-blue-600" /> Bảng xếp hạng học viên
          </h4>
          <p className="mt-1 text-sm text-slate-500">
            Top 3 theo dữ liệu trong giai đoạn đang chọn. Các bảng chỉ hiển thị học viên có dữ liệu phù hợp.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <RankingList
            title="Điểm trung bình cao nhất"
            icon={FiTrendingUp}
            rows={rankings.avgScore}
            valueText={(row) => `${row.avgPercent}%`}
            hintText={(row) => `${row.completedCount}/${row.assignedCount} bài hoàn thành`}
          />
          <RankingList
            title="Hoàn thành bài tốt nhất"
            icon={FiClipboard}
            rows={rankings.completion}
            valueText={(row) => `${row.completionRate}%`}
            hintText={(row) => `${row.completedCount}/${row.assignedCount} bài đã nộp`}
          />
          <RankingList
            title="Chuyên cần tốt nhất"
            icon={FiUserCheck}
            rows={rankings.attendance}
            valueText={(row) => `${row.attendanceRate}%`}
            hintText={(row) => `${row.attendance.present + row.attendance.late}/${row.attendance.total} buổi có mặt`}
          />
          <RankingList
            title="Tổng thời gian làm bài nhiều nhất"
            icon={FiClock}
            rows={rankings.totalDuration}
            valueText={(row) => fmtDuration(row.totalDuration)}
            hintText={(row) => `${row.attemptCount} lượt nộp · TB ${fmtDuration(row.avgDuration)}`}
          />
          <RankingList
            title="Số lượt nộp nhiều nhất"
            icon={FiBarChart2}
            rows={rankings.attempts}
            valueText={(row) => `${row.attemptCount} lượt`}
            hintText={(row) => `Tổng thời gian ${fmtDuration(row.totalDuration)}`}
          />
        </div>
      </section>

      <section className="rounded-2xl border bg-white shadow-sm">
        <div className="border-b p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h4 className="text-lg font-bold text-slate-900">Báo cáo từng học viên</h4>
              <p className="mt-1 text-sm text-slate-500">Tổng hợp chuyên cần, bài tập, điểm số và cảnh báo.</p>
            </div>
            <div className="relative w-full md:w-80">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm học viên..."
                className="w-full rounded-xl border bg-white py-2 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
          </div>
        </div>

        {filteredStudentReports.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Không có học viên phù hợp.</div>
        ) : (
          <div className="divide-y">
            {filteredStudentReports.map((row) => {
              const tone = row.status === "danger" ? "red" : row.status === "warning" ? "amber" : "green";
              return (
                <div key={row.student.id} className="p-4 md:p-5">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadge(row.status)}`}>
                          {row.warnings.length ? row.warnings.join(", ") : "Ổn định"}
                        </span>
                        {row.latest && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs text-blue-700">
                            <FiClock /> Hoạt động gần nhất: {fmtDate(row.latest.submittedAt || row.latest.updatedAt)}
                          </span>
                        )}
                      </div>

                      <h5 className="mt-2 text-base font-bold text-slate-900">{studentName(row.student)}</h5>
                      <div className="mt-1 text-sm text-slate-500">{row.student.email || row.student.display?.email || "—"}</div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 xl:w-[700px]">
                      <div className="rounded-xl border bg-slate-50 p-3">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>Chuyên cần</span>
                          <span className="font-semibold text-slate-700">{row.attendanceRate ?? "—"}%</span>
                        </div>
                        <div className="mt-2"><ProgressBar value={row.attendanceRate || 0} tone={tone} /></div>
                        <div className="mt-1 text-[11px] text-slate-500">{row.attendance.present + row.attendance.late}/{row.attendance.total || 0} buổi</div>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-3">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>Bài tập</span>
                          <span className="font-semibold text-slate-700">{row.completionRate ?? "—"}%</span>
                        </div>
                        <div className="mt-2"><ProgressBar value={row.completionRate || 0} tone={tone} /></div>
                        <div className="mt-1 text-[11px] text-slate-500">{row.completedCount}/{row.assignedCount} bài</div>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-3">
                        <div className="text-xs text-slate-500">Điểm TB</div>
                        <div className="mt-1 text-lg font-bold text-slate-900">{row.avgPercent ?? "—"}%</div>
                        <div className="text-[11px] text-slate-500">Cao nhất: {row.bestPercent ?? "—"}%</div>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-3">
                        <div className="text-xs text-slate-500">Thời gian TB</div>
                        <div className="mt-1 text-lg font-bold text-slate-900">{fmtDuration(row.avgDuration)}</div>
                        <div className="text-[11px] text-slate-500">Tổng: {fmtDuration(row.totalDuration)}</div>
                      </div>

                      <div className="rounded-xl border bg-slate-50 p-3">
                        <div className="text-xs text-slate-500">Còn thiếu</div>
                        <div className={`mt-1 text-lg font-bold ${row.missingCount ? "text-red-600" : "text-emerald-600"}`}>{row.missingCount}</div>
                        <div className="text-[11px] text-slate-500">bài chưa nộp</div>
                      </div>
                      <button
  onClick={() => setSelectedHistoryStudent(row.student)}
  className="mt-3 inline-flex items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
>
  Xem lịch sử làm bài
</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="rounded-2xl border bg-white p-4 md:p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <FiBarChart2 className="text-blue-600" /> Báo cáo bài tập/kiểm tra
              </h4>
              <p className="mt-1 text-sm text-slate-500">Tỉ lệ nộp bài và điểm trung bình theo từng assignment.</p>
            </div>
          </div>

          {assignmentReports.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed p-6 text-center text-sm text-slate-500">Chưa có bài tập/kiểm tra trong giai đoạn này.</div>
          ) : (
            <div className="mt-4 space-y-3">
              {assignmentReports.map((row) => (
                <div key={row.assignment.id} className="rounded-xl border bg-slate-50 p-3">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${row.assignment.published ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                          {row.assignment.published ? "Published" : "Draft"}
                        </span>
                        {row.overdue && <span className="inline-flex rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">Hết hạn</span>}
                      </div>
                      <div className="mt-1 font-semibold text-slate-900 line-clamp-2">{displayText(row.assignment.title, "Untitled")}</div>
                      <div className="mt-1 text-xs text-slate-500">Hạn: {fmtDate(row.assignment.deadline)} · Lượt làm: {row.attempts}</div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center md:w-80">
                      <div className="rounded-lg border bg-white px-2 py-2">
                        <div className="text-sm font-bold text-blue-700">{row.submittedCount}/{row.targetCount}</div>
                        <div className="text-[10px] text-slate-500">Đã nộp</div>
                      </div>
                      <div className="rounded-lg border bg-white px-2 py-2">
                        <div className="text-sm font-bold text-emerald-600">{row.avgPercent ?? "—"}%</div>
                        <div className="text-[10px] text-slate-500">Điểm TB</div>
                      </div>
                      <div className="rounded-lg border bg-white px-2 py-2">
                        <div className="text-sm font-bold text-blue-700">{fmtDuration(row.avgDuration)}</div>
                        <div className="text-[10px] text-slate-500">TG TB</div>
                      </div>
                      <div className="rounded-lg border bg-white px-2 py-2">
                        <div className="text-sm font-bold text-red-600">{row.missingCount}</div>
                        <div className="text-[10px] text-slate-500">Thiếu</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3"><ProgressBar value={row.completion} tone={row.completion >= 80 ? "green" : row.completion >= 50 ? "amber" : "red"} /></div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border bg-white p-4 md:p-5 shadow-sm">
          <h4 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <FiTrendingUp className="text-blue-600" /> Báo cáo nhanh cho phụ huynh
          </h4>
          <p className="mt-1 text-sm text-slate-500">Chọn học viên để tạo nội dung báo cáo có thể copy gửi phụ huynh.</p>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <select
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              className="flex-1 rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
            >
              {studentReports.map((row) => (
                <option key={row.student.id} value={row.student.id}>{studentName(row.student)}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleCopyParentReport}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <FiCopy /> Copy
            </button>
          </div>

          <textarea
            value={parentReportText}
            readOnly
            className="mt-4 min-h-[260px] w-full rounded-xl border bg-slate-50 p-3 text-sm leading-6 text-slate-700 outline-none"
          />

          <div className="mt-3 flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
            <FiCheckCircle className="mt-0.5 shrink-0" />
            Nội dung này được tạo từ dữ liệu điểm danh, bài tập và lịch sử làm bài hiện có. Giáo viên có thể chỉnh lại trước khi gửi.
          </div>
        </div>
      </section>
      <StudentHistoryDrawer
  open={!!selectedHistoryStudent}
  student={selectedHistoryStudent}
  classId={classInfo.id}
  onClose={() => setSelectedHistoryStudent(null)}
/>
    </div>
  );
}
